/* eslint-disable @typescript-eslint/prefer-for-of */

import {of as observableOf,  Observable } from 'rxjs';

import {catchError} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { ActivateAdHocEmail, AddAdHocEmail, DeactivateAdHocEmail, IAdHocEmail, UpdateAdHocEmail } from '@routes/events/models/event-mails/adhocmail-model';
import { ActivateConfirmationEmail, AddConfirmationEmail, DeactivateConfirmationEmail, UpdateConfirmationEmail } from '@routes/events/models/event-mails/confirmationmail-model';
import { ActivateInternalReminderEmail, AddInternalReminderEmail, DeactivateInternalReminderEmail, UpdateInternalReminderEmail } from '@routes/events/models/event-mails/internalremindermail-model';
import { ActivateInvitationEmail, AddInvitationEmail, DeactivateInvitationEmail, UpdateInvitationEmail } from '@routes/events/models/event-mails/invitationmail-model';
import { ActivateReminderEmail, AddReminderEmail, DeactivateReminderEmail, UpdateReminderEmail } from '@routes/events/models/event-mails/remindermail-model';
import { ActivateSaveTheDateEmail, AddSaveTheDateEmail, DeactivateSaveTheDateEmail, UpdateSaveTheDateEmail } from '@routes/events/models/event-mails/savethedatemail-model';
import { ActivateThankYouEmail, AddThankYouEmail, DeactivateThankYouEmail, UpdateThankYouEmail } from '@routes/events/models/event-mails/thankyoumail-model';
import { ActivatePaymentDocumentEmail, AddPaymentDocumentEmail, DeactivatePaymentDocumentEmail,
    UpdatePaymentDocumentEmail } from '@routes/events/models/event-mails/paymentdocumentmail-model';
import { AddTicketMail, UpdateTicketMail, ITicketMail } from '@routes/events/models/event-mails/ticketmail-model';
import { ActivateWelcomeEmail, AddWelcomeEmail, DeactivateWelcomeEmail, UpdateWelcomeEmail } from '@routes/events/models/event-mails/welcomemail-model';
import { IConfirmationEmail, IInternalReminderEmail, IInvitationEmail, IReminderEmail,
    ISaveTheDateEmail, IThankYouEmail, IWelcomeEmail, IPaymentDocumentEmail } from '@shared/services/api/legacy/eventemails-service/eventemail-model';
import { IEventEmail } from '@routes/events/models/eventmail-model';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/legacy/response';

@Injectable()
export class EventEmailsService {
    constructor(private http: HttpClient) { }

    private static get URL(): string { return `${environment.api.baseUrl}${environment.api.prefix}${EventEmailsService.ENDPOINT}`; }
    private static readonly ENDPOINT = '/timelines/{timelineId}';

    /** Invitation mail api calls. */

    public getInvitationEmail(id: string, timelineId: string): Observable<SuccessResponse<IInvitationEmail> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IInvitationEmail>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/invitationmails/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addInvitationEmail(model: AddInvitationEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content);
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content);
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        model.smsContents.forEach((smsContent, index) => {
            formData.append(`smsContents[${index}].languageId`, smsContent.languageId);
            formData.append(`smsContents[${index}].content`, smsContent.content || '');
        });

        // Add data
        const fromDate = model.fromDate.toISOString();
        const toDate = model.toDate.toISOString();
        const rsvpWithin = model.rsvpWithin + '';
        const invitationType = model.invitationType + '';
        const smsEnabled = model.smsEnabled;
        const rsvpWithinDate = model.rsvpWithinDate ? model.rsvpWithinDate.toISOString() : null;

        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRsvp = model.showRsvp;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const enableBcc = model.enableBcc;

        formData.append('fromDate', fromDate);
        formData.append('toDate', toDate);
        formData.append('rsvpWithin', `${rsvpWithin}`);
        formData.append('invitationType', `${invitationType}`);
        formData.append('smsEnabled', `${smsEnabled}`);
        formData.append('rsvpWithinDate', `${rsvpWithinDate}`);

        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRsvp', `${showRsvp}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('enableBcc', `${enableBcc}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/invitationmails/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateInvitationEmail(model: UpdateInvitationEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        for (let i = 0; i < model.currentAttachements.length; i++) {
            formData.append('currentAttachements', model.currentAttachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        model.smsContents.forEach((smsContent, index) => {
            formData.append(`smsContents[${index}].languageId`, smsContent.languageId);
            formData.append(`smsContents[${index}].content`, smsContent.content || '');
        });

        const id = model.id;
        const fromDate = model.fromDate.toISOString();
        const toDate = model.toDate.toISOString();
        const rsvpWithin = model.rsvpWithin + '';
        const invitationType = model.invitationType + '';
        const smsEnabled = model.smsEnabled;
        const rsvpWithinDate = model.rsvpWithinDate ? model.rsvpWithinDate.toISOString() : null;

        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRsvp = model.showRsvp;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const enableBcc = model.enableBcc;

        formData.append('id', id);
        formData.append('fromDate', fromDate);
        formData.append('toDate', toDate);
        formData.append('rsvpWithin', `${rsvpWithin}`);
        formData.append('invitationType', `${invitationType}`);
        formData.append('smsEnabled', `${smsEnabled}`);
        formData.append('rsvpWithinDate', `${rsvpWithinDate}`);

        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRsvp', `${showRsvp}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('enableBcc', `${enableBcc}`);

        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/invitationmails/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivateInvitationEmail(model: DeactivateInvitationEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/invitationmails/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activateInvitationEmail(model: ActivateInvitationEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/invitationmails/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteInvitationEmail(timelineId: string, mailId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/invitationmails/' + mailId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public previewInvitationEmail(model: AddInvitationEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        const rsvpWithin = model.rsvpWithin;
        const rsvpWithinDate = model.rsvpWithinDate ? model.rsvpWithinDate.toISOString() : null;
        const id = model.id;
        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRsvp = model.showRsvp;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;

        const languageId = model.languageId;

        model.userIds.forEach((userId, index) => {
            formData.append(`userIds[${index}]`, userId);
        });

        formData.append('rsvpWithin', `${rsvpWithin}`);
        formData.append('rsvpWithinDate', `${rsvpWithinDate}`);
        formData.append('id', id);
        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRsvp', `${showRsvp}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);

        formData.append('languageId', languageId);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/invitationmails/preview', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Confirmation mail api calls. */

    public getConfirmationEmail(id: string, timelineId: string): Observable<SuccessResponse<IConfirmationEmail> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IConfirmationEmail>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/confirmationmails/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addConfirmationEmail(model: AddConfirmationEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showWebinarButton = model.showWebinarButton;
        const showTicketButton = model.showTicketButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const showRegistration = model.showRegistration;
        const enableBcc = model.enableBcc;
        const showGalleryButton = model.showGalleryButton;

        const confirmationSenderType = model.confirmationSenderType;

        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showWebinarButton', `${showWebinarButton}`);
        formData.append('showTicketButton', `${showTicketButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('showRegistration', `${showRegistration}`);
        formData.append('enableBcc', `${enableBcc}`);
        formData.append('showGalleryButton', `${showGalleryButton}`);

        formData.append('confirmationSenderType', `${confirmationSenderType}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/confirmationmails/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateConfirmationEmail(model: UpdateConfirmationEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        for (let i = 0; i < model.currentAttachements.length; i++) {
            formData.append('currentAttachements', model.currentAttachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        const id = model.id;

        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showWebinarButton = model.showWebinarButton;
        const showTicketButton = model.showTicketButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const showRegistration = model.showRegistration;
        const enableBcc = model.enableBcc;
        const showGalleryButton = model.showGalleryButton;

        const confirmationSenderType = model.confirmationSenderType;

        formData.append('id', id);

        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showWebinarButton', `${showWebinarButton}`);
        formData.append('showTicketButton', `${showTicketButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('showRegistration', `${showRegistration}`);
        formData.append('enableBcc', `${enableBcc}`);
        formData.append('showGalleryButton', `${showGalleryButton}`);

        formData.append('confirmationSenderType', `${confirmationSenderType}`);

        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/confirmationmails/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivateConfirmationEmail(model: DeactivateConfirmationEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/confirmationmails/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activateConfirmationEmail(model: ActivateConfirmationEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/confirmationmails/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteConfirmationEmail(timelineId: string, mailId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/confirmationmails/' + mailId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public previewConfirmationEmail(model: AddConfirmationEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        const id = model.id;
        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showWebinarButton = model.showWebinarButton;
        const showTicketButton = model.showTicketButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const showRegistration = model.showRegistration;
        const showGalleryButton = model.showGalleryButton;

        const languageId = model.languageId;

        model.userIds.forEach((userId, index) => {
            formData.append(`userIds[${index}]`, userId);
        });

        formData.append('id', id);
        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showWebinarButton', `${showWebinarButton}`);
        formData.append('showTicketButton', `${showTicketButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('showRegistration', `${showRegistration}`);
        formData.append('showGalleryButton', `${showGalleryButton}`);

        formData.append('languageId', `${languageId}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/confirmationmails/preview', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Reminder mail api calls. */

    public getReminderEmail(id: string, timelineId: string): Observable<SuccessResponse<IReminderEmail> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IReminderEmail>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/remindermails/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addReminderEmail(model: AddReminderEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        model.smsContents.forEach((subject, index) => {
            formData.append(`smsContents[${index}].languageId`, subject.languageId);
            formData.append(`smsContents[${index}].content`, subject.content || '');
        });

        model.userIds.forEach((userId, index) => {
            formData.append(`userIds[${index}]`, userId);
        });

        const smsEnabled = model.smsEnabled;
        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const enableBcc = model.enableBcc;
        const reminderWithin = model.reminderWithin;
        const showRsvp = model.showRsvp;

        formData.append('smsEnabled', `${smsEnabled}`);
        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('enableBcc', `${enableBcc}`);
        formData.append('reminderWithin', `${reminderWithin}`);
        formData.append('showRsvp', `${showRsvp}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/remindermails/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateReminderEmail(model: UpdateReminderEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        for (let i = 0; i < model.currentAttachements?.length; i++) {
            formData.append('currentAttachements', model.currentAttachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        model.smsContents.forEach((smsContent, index) => {
            formData.append(`smsContents[${index}].languageId`, smsContent.languageId);
            formData.append(`smsContents[${index}].content`, smsContent.content || '');
        });

        const id = model.id;
        const smsEnabled = model.smsEnabled;
        const reminderWithin = model.reminderWithin;
        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRsvp = model.showRsvp;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const enableBcc = model.enableBcc;

        formData.append('id', id);
        formData.append('smsEnabled', `${smsEnabled}`);
        formData.append('reminderWithin', `${reminderWithin}`);
        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRsvp', `${showRsvp}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('enableBcc', `${enableBcc}`);


        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/remindermails/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivateReminderEmail(model: DeactivateReminderEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/remindermails/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activateReminderEmail(model: ActivateReminderEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/remindermails/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteReminderEmail(timelineId: string, mailId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/remindermails/' + mailId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public previewReminderEmail(model: AddReminderEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();

        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }
        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        model.smsContents.forEach((subject, index) => {
            formData.append(`smsContents[${index}].languageId`, subject.languageId);
            formData.append(`smsContents[${index}].content`, subject.content || '');
        });

        model.userIds.forEach((userId, index) => {
            formData.append(`userIds[${index}]`, userId);
        });

        const languageId = model.languageId;

        const smsEnabled = model.smsEnabled;
        const showLogo = model.showLogo;
        const id = model.id;
        const showBanner = model.showBanner;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const enableBcc = model.enableBcc;
        const reminderWithin = model.reminderWithin;
        const showRsvp = model.showRsvp;

        formData.append('languageId', languageId);
        formData.append('smsEnabled', `${smsEnabled}`);
        formData.append('showLogo', `${showLogo}`);
        formData.append('id', id);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('enableBcc', `${enableBcc}`);
        formData.append('reminderWithin', `${reminderWithin}`);
        formData.append('showRsvp', `${showRsvp}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/remindermails/preview', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Internal reminder mail api calls. */

    public getInternalReminderEmail(id: string, timelineId: string): Observable<SuccessResponse<IInternalReminderEmail> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IInternalReminderEmail>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/internalremindermails/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addInternalReminderEmail(model: AddInternalReminderEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/internalremindermails/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateInternalReminderEmail(model: UpdateInternalReminderEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/internalremindermails/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivateInternalReminderEmail(model: DeactivateInternalReminderEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/internalremindermails/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteInternalReminderEmail(timelineId: string, mailId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/internalremindermails/' + mailId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activateInternalReminderEmail(model: ActivateInternalReminderEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/internalremindermails/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Welcome mail api calls. */

    public getWelcomeEmail(id: string, timelineId: string): Observable<SuccessResponse<IWelcomeEmail> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IWelcomeEmail>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/welcomemails/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addWelcomeEmail(model: AddWelcomeEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }


        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        model.smsContents.forEach((subject, index) => {
            formData.append(`smsContents[${index}].languageId`, subject.languageId);
            formData.append(`smsContents[${index}].content`, subject.content || '');
        });

        const smsEnabled = model.smsEnabled;
        const daysBefore = model.daysBefore;

        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showWebinarButton = model.showWebinarButton;
        const showTicketButton = model.showTicketButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const showRegistration = model.showRegistration;
        const enableBcc = model.enableBcc;
        const showGalleryButton = model.showGalleryButton;

        const welcomeSenderType = model.welcomeSenderType;

        formData.append('smsEnabled', `${smsEnabled}`);
        formData.append('daysBefore', `${daysBefore}`);
        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showWebinarButton', `${showWebinarButton}`);
        formData.append('showTicketButton', `${showTicketButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('showRegistration', `${showRegistration}`);
        formData.append('enableBcc', `${enableBcc}`);
        formData.append('showGalleryButton', `${showGalleryButton}`);

        formData.append('welcomeSenderType', `${welcomeSenderType}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/welcomemails/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateWelcomeEmail(model: UpdateWelcomeEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        for (let i = 0; i < model.currentAttachements.length; i++) {
            formData.append('currentAttachements', model.currentAttachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        model.smsContents.forEach((subject, index) => {
            formData.append(`smsContents[${index}].languageId`, subject.languageId);
            formData.append(`smsContents[${index}].content`, subject.content || '');
        });

        const smsEnabled = model.smsEnabled;
        const daysBefore = model.daysBefore;
        const id = model.id;

        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showWebinarButton = model.showWebinarButton;
        const showTicketButton = model.showTicketButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const showRegistration = model.showRegistration;
        const enableBcc = model.enableBcc;
        const showGalleryButton = model.showGalleryButton;

        const welcomeSenderType = model.welcomeSenderType;

        formData.append('smsEnabled', `${smsEnabled}`);
        formData.append('daysBefore', `${daysBefore}`);
        formData.append('id', `${id}`);

        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showWebinarButton', `${showWebinarButton}`);
        formData.append('showTicketButton', `${showTicketButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('showRegistration', `${showRegistration}`);
        formData.append('enableBcc', `${enableBcc}`);
        formData.append('showGalleryButton', `${showGalleryButton}`);

        formData.append('welcomeSenderType',`${welcomeSenderType}`);

        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/welcomemails/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivateWelcomeEmail(model: DeactivateWelcomeEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/welcomemails/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activateWelcomeEmail(model: ActivateWelcomeEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/welcomemails/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteWelcomeEmail(timelineId: string, mailId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/welcomemails/' + mailId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public previewWelcomeEmail(model: AddWelcomeEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add data

        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        const id = model.id;
        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showWebinarButton = model.showWebinarButton;
        const showTicketButton = model.showTicketButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const showRegistration = model.showRegistration;
        const showGalleryButton = model.showGalleryButton;

        const languageId = model.languageId;

        model.userIds.forEach((userId, index) => {
            formData.append(`userIds[${index}]`, userId);
        });

        formData.append('id', id);
        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showWebinarButton', `${showWebinarButton}`);
        formData.append('showTicketButton', `${showTicketButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('showRegistration', `${showRegistration}`);
        formData.append('showGalleryButton', `${showGalleryButton}`);

        formData.append('languageId', `${languageId}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/welcomemails/preview', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Thank you mail api calls. */

    public getThankYouEmail(id: string, timelineId: string): Observable<SuccessResponse<IThankYouEmail> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IThankYouEmail>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/thankyoumails/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addThankYouEmail(model: AddThankYouEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        const daysAfter = model.daysAfter;

        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showSurveyButton = model.showSurveyButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const enableBcc = model.enableBcc;
        const showGalleryButton = model.showGalleryButton;

        const thankYouSenderType = model.thankYouSenderType;

        formData.append('daysAfter', `${daysAfter}`);

        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showSurveyButton', `${showSurveyButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('enableBcc', `${enableBcc}`);
        formData.append('showGalleryButton', `${showGalleryButton}`);
        formData.append('showDocumentButton', `${model.showDocumentButton}`);

        formData.append('thankYouSenderType', `${thankYouSenderType}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/thankyoumails/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateThankYouEmail(model: UpdateThankYouEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        for (let i = 0; i < model.currentAttachements.length; i++) {
            formData.append('currentAttachements', model.currentAttachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        const daysAfter = model.daysAfter;
        const id = model.id;

        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showSurveyButton = model.showSurveyButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const enableBcc = model.enableBcc;
        const showGalleryButton = model.showGalleryButton;

        const thankYouSenderType = model.thankYouSenderType;

        formData.append('daysAfter', `${daysAfter}`);
        formData.append('id', id);

        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showSurveyButton', `${showSurveyButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('enableBcc', `${enableBcc}`);
        formData.append('showGalleryButton', `${showGalleryButton}`);
        formData.append('showDocumentButton', `${model.showDocumentButton}`);

        formData.append('thankYouSenderType', `${thankYouSenderType}`);

        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/thankyoumails/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivateThankYouEmail(model: DeactivateThankYouEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/thankyoumails/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activateThankYouEmail(model: ActivateThankYouEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/thankyoumails/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteThankYouEmail(timelineId: string, mailId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/thankyoumails/' + mailId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public previewThankYouEmail(model: AddThankYouEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add  data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        const id = model.id;
        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showSurveyButton = model.showSurveyButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showGalleryButton = model.showGalleryButton;

        const languageId = model.languageId;

        model.userIds.forEach((userId, index) => {
            formData.append(`userIds[${index}]`, userId);
        });

        formData.append('id', id);
        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showSurveyButton', `${showSurveyButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showGalleryButton', `${showGalleryButton}`);
        formData.append('showDocumentButton', `${model.showDocumentButton}`);

        formData.append('languageId', languageId);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/thankyoumails/preview', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Payment Document mail api calls. */

    public getPaymentDocumentEmail(id: string, timelineId: string): Observable<SuccessResponse<IEventEmail> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IEventEmail>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/paymentdocumentsmail/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addPaymentDocumentEmail(model: AddPaymentDocumentEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        if (model.contentsTop && model.contentsTop.length > 0) {
            model.contentsTop.forEach((content, index) => {
                formData.append(`contentsTop[${index}].index`, `${index}`);
                formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                        if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                            content.contentsLanguages.forEach((contentLanguage, index2) => {
                                formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                                formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                            });
                        } else {
                            formData.append(`contentsTop[${index}].contentsLanguages[0].languageId`, '');
                            formData.append(`contentsTop[${index}].contentsLanguages[0].content`, '');
                        }
            });
        } else {
            formData.append('contentsTop', 'null');
        }

        if (model.contentsBottom && model.contentsBottom.length > 0) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                        if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                            content.contentsLanguages.forEach((contentLanguage, index2) => {
                                formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                                formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                            });
                        } else {
                            formData.append(`contentsBottom[${index}].contentsLanguages[0].languageId`, '');
                            formData.append(`contentsBottom[${index}].contentsLanguages[0].content`, '');
                        }
            });
        } else {
            formData.append('contentsBottom', null);
        }

                model.subjects.forEach((subject, index) => {
                    formData.append(`subjects[${index}].languageId`, subject.languageId);
                    formData.append(`subjects[${index}].content`, subject.content || '');
                });

        formData.append('showLogo', `${model.showLogo}`);
        formData.append('showBanner', `${model.showBanner}`);
        formData.append('showDetail', `${model.showDetail}`);
        formData.append('showContact', `${model.showContact}`);
        formData.append('enableBcc', `${model.enableBcc}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/paymentdocumentsmail/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updatePaymentDocumentEmail(model: UpdatePaymentDocumentEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        const formData: FormData = new FormData();
        formData.append('id', model.id);
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        for (let i = 0; i < model.currentAttachements.length; i++) {
            formData.append('currentAttachements', model.currentAttachements[i]);
        }

        if (model.contentsTop && model.contentsTop.length > 0) {
            model.contentsTop.forEach((content, index) => {
                formData.append(`contentsTop[${index}].index`, `${index}`);
                formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                        if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                            content.contentsLanguages.forEach((contentLanguage, index2) => {
                                formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                                formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                            });
                        } else {
                            formData.append(`contentsTop[${index}].contentsLanguages[0].languageId`, '');
                            formData.append(`contentsTop[${index}].contentsLanguages[0].content`, '');
                        }
            });
        } else {
            formData.append('contentsTop', 'null');
        }

        if (model.contentsBottom && model.contentsBottom.length > 0) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                        if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                            content.contentsLanguages.forEach((contentLanguage, index2) => {
                                formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                                formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                            });
                        } else {
                            formData.append(`contentsBottom[${index}].contentsLanguages[0].languageId`, '');
                            formData.append(`contentsBottom[${index}].contentsLanguages[0].content`, '');
                        }
            });
        } else {
            formData.append('contentsBottom', 'null');
        }

                model.subjects.forEach((subject, index) => {
                    formData.append(`subjects[${index}].languageId`, subject.languageId);
                    formData.append(`subjects[${index}].content`, subject.content || '');
                });

        formData.append('showLogo', `${model.showLogo}`);
        formData.append('showBanner', `${model.showBanner}`);
        formData.append('showDetail', `${model.showDetail}`);
        formData.append('showContact', `${model.showContact}`);
        formData.append('enableBcc', `${model.enableBcc}`);

        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/paymentdocumentsmail/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivatePaymentDocumentEmail(model: DeactivatePaymentDocumentEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/paymentdocumentsmail/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activatePaymentDocumentEmail(model: ActivatePaymentDocumentEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/paymentdocumentsmail/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deletePaymentDocumentEmail(timelineId: string, mailId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/paymentdocumentsmail/' + mailId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public previewPaymentDocumentEmail(model: AddPaymentDocumentEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        if (model.contentsTop && model.contentsTop.length > 0) {
            model.contentsTop.forEach((content, index) => {
                formData.append(`contentsTop[${index}].index`, `${index}`);
                formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                        if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                            content.contentsLanguages.forEach((contentLanguage, index2) => {
                                formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                                formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                            });
                        } else {
                            formData.append(`contentsTop[${index}].contentsLanguages[0].languageId`, '');
                            formData.append(`contentsTop[${index}].contentsLanguages[0].content`, '');
                        }
            });
        } else {
            formData.append('contentsTop', 'null');
        }

        if (model.contentsBottom && model.contentsBottom.length > 0) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                        if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                            content.contentsLanguages.forEach((contentLanguage, index2) => {
                                formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                                formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                            });
                        } else {
                            formData.append(`contentsBottom[${index}].contentsLanguages[0].languageId`, '');
                            formData.append(`contentsBottom[${index}].contentsLanguages[0].content`, '');
                        }
            });
        } else {
            formData.append('contentsBottom', 'null');
        }

                model.subjects.forEach((subject, index) => {
                    formData.append(`subjects[${index}].languageId`, subject.languageId);
                    formData.append(`subjects[${index}].content`, subject.content || '');
                });

        formData.append('showLogo', `${model.showLogo}`);
        formData.append('showBanner', `${model.showBanner}`);
        formData.append('showDetail', `${model.showDetail}`);
        formData.append('showContact', `${model.showContact}`);
        formData.append('enableBcc', `${model.enableBcc}`);
        formData.append('languageId', model.languageId);
        formData.append('id', model.id || null);
        model.userIds.forEach((userId, index) => {
            formData.append(`userIds[${index}]`, userId);
        });

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/paymentdocumentsmail/preview', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

     /** Ad hoc mail api calls. */

    public getAdHocEmail(id: string, timelineId: string): Observable<SuccessResponse<IAdHocEmail> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IAdHocEmail>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/adhocmails/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addAdHocEmail(model: AddAdHocEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        model.locationDateTimeIds.forEach((element, index) => {
            formData.append(`locationDateTimeIds[${index}]`, element);
        });

        const targetGroupType = model.targetGroupType;
        const sendDate = model.sendDate?.toISOString();

        const name = model.name;
        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRsvp = model.showRsvp;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showWebinarButton = model.showWebinarButton;
        const showSurveyButton = model.showSurveyButton;
        const showTicketButton = model.showTicketButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const showRegistration = model.showRegistration;
        const enableBcc = model.enableBcc;
        const showGalleryButton = model.showGalleryButton;

        const adHocSenderType = model.adHocSenderType;

        formData.append('targetGroupType', `${targetGroupType}`);
        formData.append('sendDate', `${sendDate}`);

        formData.append('name', name);
        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRsvp', `${showRsvp}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showWebinarButton', `${showWebinarButton}`);
        formData.append('showSurveyButton', `${showSurveyButton}`);
        formData.append('showTicketButton', `${showTicketButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('showRegistration', `${showRegistration}`);
        formData.append('enableBcc', `${enableBcc}`);
        formData.append('showGalleryButton', `${showGalleryButton}`);
        formData.append('showDocumentButton', `${model.showDocumentButton}`);

        formData.append('adHocSenderType', `${adHocSenderType}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/adhocmails/add', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateAdHocEmail(model: UpdateAdHocEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        for (let i = 0; i < model.currentAttachements.length; i++) {
            formData.append('currentAttachements', model.currentAttachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        model.locationDateTimeIds.forEach((element, index) => {
            formData.append(`locationDateTimeIds[${index}]`, element);
        });

        const id = model.id;
        const targetGroupType = model.targetGroupType;


        const sendDate = model.sendDate?.toISOString();

        const name = model.name;
        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRsvp = model.showRsvp;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showWebinarButton = model.showWebinarButton;
        const showSurveyButton = model.showSurveyButton;
        const showTicketButton = model.showTicketButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const showRegistration = model.showRegistration;
        const enableBcc = model.enableBcc;
        const showGalleryButton = model.showGalleryButton;

        const adHocSenderType = model.adHocSenderType;

        formData.append('id', id);
        formData.append('targetGroupType', `${targetGroupType}`);
        formData.append('sendDate', `${sendDate}`);

        formData.append('name', name);
        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRsvp', `${showRsvp}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showWebinarButton', `${showWebinarButton}`);
        formData.append('showSurveyButton', `${showSurveyButton}`);
        formData.append('showTicketButton', `${showTicketButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('showRegistration', `${showRegistration}`);
        formData.append('enableBcc', `${enableBcc}`);
        formData.append('showGalleryButton', `${showGalleryButton}`);
        formData.append('showDocumentButton', `${model.showDocumentButton}`);

        formData.append('adHocSenderType', `${adHocSenderType}`);

        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/adhocmails/update', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivateAdHocEmail(model: DeactivateAdHocEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/adhocmails/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activateAdHocEmail(model: ActivateAdHocEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/adhocmails/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteAdHocEmail(timelineId: string, mailId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/adhocmails/' + mailId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public previewAdHocEmail(model: AddAdHocEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        model.locationDateTimeIds.forEach((element, index) => {
            formData.append(`locationDateTimeIds[${index}]`, element);
        });

        const targetGroupType = model.targetGroupType;
        const id = model.id;
        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showRsvp = model.showRsvp;
        const showRegistrationButton = model.showRegistrationButton;
        const showInfoButton = model.showInfoButton;
        const showWebinarButton = model.showWebinarButton;
        const showSurveyButton = model.showSurveyButton;
        const showTicketButton = model.showTicketButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const showRegistration = model.showRegistration;
        const showGalleryButton = model.showGalleryButton;

        const languageId = model.languageId;

        model.userIds.forEach((userId, index) => {
            formData.append(`userIds[${index}]`, userId);
        });

        formData.append('targetGroupType', `${targetGroupType}`);
        formData.append('id', id);
        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showRsvp', `${showRsvp}`);
        formData.append('showRegistrationButton', `${showRegistrationButton}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showWebinarButton', `${showWebinarButton}`);
        formData.append('showSurveyButton', `${showSurveyButton}`);
        formData.append('showTicketButton', `${showTicketButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('showRegistration', `${showRegistration}`);
        formData.append('showGalleryButton', `${showGalleryButton}`);
        formData.append('showDocumentButton', `${model.showDocumentButton}`);

        formData.append('languageId', languageId);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/adhocmails/preview', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

        /** TicketM mail api calls. */

    public getTicketMail(mailId: string, timelineId: string): Observable<SuccessResponse<ITicketMail> | ErrorResponse> {
    return this.http
        .get<SuccessResponse<ITicketMail>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/ticketmail/' + mailId).pipe(
        catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addTicketMail(model: AddTicketMail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        const formData: FormData = new FormData();

        // Add files
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        model.smsContents.forEach((smsContent, index) => {
            formData.append(`smsContents[${index}].languageId`, smsContent.languageId);
            formData.append(`smsContents[${index}].content`, smsContent.content || '');
        });

        const ticketMailType = model.ticketMailType;
        const daysBefore = model.daysBefore;
        const smsEnabled = model.smsEnabled;

        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showInfoButton = model.showInfoButton;
        const showTicketButton = model.showTicketButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const showRegistration = model.showRegistration;
        const enableBcc = model.enableBcc;

        const ticketSenderType = model.ticketSenderType;

        formData.append('ticketMailType', `${ticketMailType}`);
        formData.append('daysBefore', `${daysBefore}`);
        formData.append('smsEnabled', `${smsEnabled}`);

        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showTicketButton', `${showTicketButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('showRegistration', `${showRegistration}`);
        formData.append('enableBcc', `${enableBcc}`);

        formData.append('ticketSenderType', `${ticketSenderType}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/ticketmail', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateTicketMail(model: UpdateTicketMail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        const formData: FormData = new FormData();

        // Add files
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        for (let i = 0; i < model.currentAttachements?.length; i++) {
            formData.append('currentAttachements', model.currentAttachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        model.smsContents.forEach((subject, index) => {
            formData.append(`smsContents[${index}].languageId`, subject.languageId);
            formData.append(`smsContents[${index}].content`, subject.content || '');
        });

        const id = model.id;
        const ticketMailType = model.ticketMailType;
        const daysBefore = model.daysBefore;
        const smsEnabled = model.smsEnabled;

        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showInfoButton = model.showInfoButton;
        const showTicketButton = model.showTicketButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const showRegistration = model.showRegistration;
        const enableBcc = model.enableBcc;

        const ticketSenderType = model.ticketSenderType;

        formData.append('ticketMailId', id);
        formData.append('ticketMailType', `${ticketMailType}`);
        formData.append('daysBefore', `${daysBefore}`);
        formData.append('smsEnabled', `${smsEnabled}`);

        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showTicketButton', `${showTicketButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('showRegistration', `${showRegistration}`);
        formData.append('enableBcc', `${enableBcc}`);

        formData.append('ticketSenderType', `${ticketSenderType}`);

        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/ticketmail', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteTicketEmail(timelineId: string, mailId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/ticketmails/' + mailId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public previewTicketMail(model: AddTicketMail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        const formData: FormData = new FormData();
        // Add files
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }
        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });

        if (model.showContentBottom) {
            model.contentsBottom.forEach((content, index) => {
                formData.append(`contentsBottom[${index}].index`, `${index}`);
                formData.append(`contentsBottom[${index}].logicalOperator`, `${content.logicalOperator}`);
                if (content.groupIds && content.groupIds.length > 0) {
                    content.groupIds.forEach((groupId, index2) => {
                        formData.append(`contentsBottom[${index}].groupIds[${index2}]`, groupId);
                    });
                }
                if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                    content.locationDateTimeIds.forEach((venueId, index2) => {
                        formData.append(`contentsBottom[${index}].locationDateTimeIds[${index2}]`, venueId);
                    });
                }
                if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                    content.contentsLanguages.forEach((contentLanguage, index2) => {
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                        formData.append(`contentsBottom[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                    });
                }
            });
        }

        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });
        const id = model.id;
        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showInfoButton = model.showInfoButton;
        const showTicketButton = model.showTicketButton;
        const showContentBottom = model.showContentBottom;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const showRegistration = model.showRegistration;

        const languageId = model.languageId;

        model.userIds.forEach((userId, index) => {
            formData.append(`userIds[${index}]`, userId);
        });

        formData.append('id', id);
        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showInfoButton', `${showInfoButton}`);
        formData.append('showTicketButton', `${showTicketButton}`);
        formData.append('showContentBottom', `${showContentBottom}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('showRegistration', `${showRegistration}`);

        formData.append('languageId', languageId);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/ticketmail/preview', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Save the date mail api calls. */

    public getSaveTheDateEmail(id: string, timelineId: string): Observable<SuccessResponse<ISaveTheDateEmail> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<ISaveTheDateEmail>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/savedatemails/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addSaveTheDateEmail(model: AddSaveTheDateEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });
        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        const sendDate = model.sendDate?.toISOString();

        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const enableBcc = model.enableBcc;

        formData.append('sendDate', `${sendDate}`);

        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('enableBcc', `${enableBcc}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/savedatemails/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateSaveTheDateEmail(model: UpdateSaveTheDateEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        for (let i = 0; i < model.currentAttachements.length; i++) {
            formData.append('currentAttachements', model.currentAttachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });
        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });

        const sendDate = model.sendDate?.toISOString();
        const id = model.id;

        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;
        const enableBcc = model.enableBcc;

        formData.append('sendDate', `${sendDate}`);
        formData.append('id', id);

        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);
        formData.append('enableBcc', `${enableBcc}`);

        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/savedatemails/', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivateSaveTheDateEmail(model: DeactivateSaveTheDateEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/savedatemails/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activateSaveTheDateEmail(model: ActivateSaveTheDateEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/savedatemails/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteSaveTheDateEmail(timelineId: string, mailId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/savedatemails/' + mailId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public previewSaveTheDateEmail(model: AddSaveTheDateEmail, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {

        // Add files
        const formData: FormData = new FormData();
        for (let i = 0; i < model.attachements.length; i++) {
            formData.append('attachements', model.attachements[i]);
        }

        // Add data
        model.contentsTop.forEach((content, index) => {
            formData.append(`contentsTop[${index}].index`, `${index}`);
            formData.append(`contentsTop[${index}].logicalOperator`, `${content.logicalOperator}`);
            if (content.groupIds && content.groupIds.length > 0) {
                content.groupIds.forEach((groupId, index2) => {
                    formData.append(`contentsTop[${index}].groupIds[${index2}]`, groupId);
                });
            }
            if (content.locationDateTimeIds && content.locationDateTimeIds.length > 0) {
                content.locationDateTimeIds.forEach((venueId, index2) => {
                    formData.append(`contentsTop[${index}].locationDateTimeIds[${index2}]`, venueId);
                });
            }
            if (content.contentsLanguages && content.contentsLanguages.length > 0) {
                content.contentsLanguages.forEach((contentLanguage, index2) => {
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].languageId`, contentLanguage.languageId);
                    formData.append(`contentsTop[${index}].contentsLanguages[${index2}].content`, contentLanguage.content || '');
                });
            }
        });
        model.subjects.forEach((subject, index) => {
            formData.append(`subjects[${index}].languageId`, subject.languageId);
            formData.append(`subjects[${index}].content`, subject.content || '');
        });
        const id = model.id;
        const showLogo = model.showLogo;
        const showBanner = model.showBanner;
        const showDetail = model.showDetail;
        const showContact = model.showContact;
        const showCalendar = model.showCalendar;

        const languageId = model.languageId;

        model.userIds.forEach((userId, index) => {
            formData.append(`userIds[${index}]`, userId);
        });

        formData.append('id', id);
        formData.append('showLogo', `${showLogo}`);
        formData.append('showBanner', `${showBanner}`);
        formData.append('showDetail', `${showDetail}`);
        formData.append('showContact', `${showContact}`);
        formData.append('showCalendar', `${showCalendar}`);

        formData.append('languageId', `${languageId}`);

        return this.http
            .post<SuccessResponse<boolean>>(EventEmailsService.URL.replace('{timelineId}', timelineId) + '/savedatemails/preview', formData).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }
}
